import React, { useState } from "react";
import { useStore } from "react-redux";
import CommunicationService from "../services/CommunicationService"; // adjust path if needed
import styles from "./QueryBuilder.module.css";

const COMMON_PARAMS = [
    { value: "subject", label: "Patient (subject)" },
    { value: "sender", label: "Sender (Practitioner)" },
    { value: "category", label: "Category" },
    { value: "sent", label: "Sent (date range)" },
];

// Category options for dropdown
const CATEGORY_MAP = {
    Alert: "http://terminology.hl7.org/CodeSystem/communication-category|alert",
    Notification: "http://terminology.hl7.org/CodeSystem/communication-category|notification",
    Reminder: "http://terminology.hl7.org/CodeSystem/communication-category|reminder",
    Instruction: "http://terminology.hl7.org/CodeSystem/communication-category|instruction",
};

export default function QueryBuilder({ fhirClient, contextProvider, onClose, setSelectedComm }) {
    const store = useStore();
    const [loading, setLoading] = useState(false);
    const [params, setParams] = useState([]);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchedResources, setSearchedResources] = useState([]);
    const [activeParamIndex, setActiveParamIndex] = useState(null);
    const [activeParamField, setActiveParamField] = useState(null);

    const [subjectDisplayNames, setSubjectDisplayNames] = useState({});
    const [providerDisplayNames, setProviderDisplayNames] = useState({});

    const [direction, setDirection] = useState("received"); // UI-only

    const communicationService = React.useMemo(() => {
        if (!fhirClient) return null;
        return new CommunicationService(fhirClient);
    }, [fhirClient]);

    const addParam = () => {
        const usedFields = params.map((p) => p.field).filter(Boolean);
        const availableFields = COMMON_PARAMS.map((opt) => opt.value).filter(
            (field) => !usedFields.includes(field)
        );
        if (availableFields.length === 0) {
            setError("All available criteria have already been added.");
            return;
        }
        const nextField = availableFields[0];
        setParams([...params, { field: nextField, value: "", startDate: "", endDate: "" }]);
    };

    const updateParam = (index, key, val) => {
        const updated = [...params];
        updated[index][key] = val;
        setParams(updated);

        if (key === "field") {
            if (val !== "subject" && subjectDisplayNames[index]) {
                setSubjectDisplayNames((prev) => {
                    const copy = { ...prev };
                    delete copy[index];
                    return copy;
                });
            }
            if (val !== "sender" && providerDisplayNames[index]) {
                setProviderDisplayNames((prev) => {
                    const copy = { ...prev };
                    delete copy[index];
                    return copy;
                });
            }
            if (index === activeParamIndex) {
                setActiveParamIndex(null);
                setSearchTerm("");
                setSearchedResources([]);
                setActiveParamField(null);
            }
        }
    };

    const removeParam = (index) => {
        const updated = [...params];
        updated.splice(index, 1);
        setParams(updated);

        if (index === activeParamIndex) {
            setActiveParamIndex(null);
            setSearchTerm("");
            setSearchedResources([]);
            setActiveParamField(null);
        }

        setSubjectDisplayNames((prev) => {
            const copy = { ...prev };
            delete copy[index];
            return copy;
        });
        setProviderDisplayNames((prev) => {
            const copy = { ...prev };
            delete copy[index];
            return copy;
        });
    };

    const handleSearchByName = async () => {
        if (!searchTerm.trim()) {
            setError(`Please enter a ${activeParamField === "subject" ? "subject" : "provider"} name.`);
            setSearchedResources([]);
            return;
        }
        if (!fhirClient) {
            setError("FHIR Client is not initialized.");
            setSearchedResources([]);
            return;
        }
        try {
            let resourceType = activeParamField === "sender" ? "Practitioner" : "Patient";
            const searchResults = await fhirClient.request(
                `${resourceType}?name=${encodeURIComponent(searchTerm)}`
            );
            const found = searchResults.entry?.map((e) => e.resource) || [];
            setSearchedResources(found);
            setError(found.length ? null : `No ${resourceType.toLowerCase()}s found.`);
        } catch (err) {
            console.error(`Error searching ${activeParamField}:`, err);
            setError(`Error searching ${activeParamField}.`);
            setSearchedResources([]);
        }
    };

    const handleSelectResource = (resource, index) => {
        const refPrefix = activeParamField === "sender" ? "Practitioner" : "Patient";
        const displayName = `${resource.name?.[0]?.given?.join(" ") || ""} ${resource.name?.[0]?.family || ""}`.trim();
        const resourceRef = `${refPrefix}/${resource.id}`;

        const updated = [...params];
        updated[index].value = resourceRef;
        setParams(updated);

        if (activeParamField === "subject") {
            setSubjectDisplayNames((prev) => ({ ...prev, [index]: displayName }));
        } else if (activeParamField === "sender") {
            setProviderDisplayNames((prev) => ({ ...prev, [index]: displayName }));
        }

        setSearchTerm("");
        setSearchedResources([]);
        setActiveParamIndex(null);
        setActiveParamField(null);
        setError(null);
    };

    const buildSearchParams = () => {
        const paramStrings = [];

        params.forEach((p) => {
            if (!p.field) return;
            if (p.field === "sent") {
                if (p.startDate) paramStrings.push(`sent=ge${encodeURIComponent(p.startDate)}`);
                if (p.endDate) paramStrings.push(`sent=le${encodeURIComponent(p.endDate)}`);
            } else if (p.value) {
                paramStrings.push(`${encodeURIComponent(p.field)}=${encodeURIComponent(p.value)}`);
            }
        });

        if (contextProvider?.id) {
            if (direction === "sent") {
                paramStrings.push(`sender=Practitioner/${contextProvider.id}`);
            } else {
                paramStrings.push(`recipient=Practitioner/${contextProvider.id}`);
            }
        }

        return paramStrings.join("&");
    };

    const runQuery = async () => {
        if (!communicationService) return;

        setLoading(true);
        setError(null);

        try {
            const searchParams = buildSearchParams();
            const results = await communicationService.getCommunicationsPage(
                contextProvider?.id,
                10,
                null,
                direction,
                store,
                searchParams,
                true
            );

            if (!results.data || results.total === 0) {
                setError("No results found. Try adjusting your search criteria.");
            } else {
                setError(null);
                setSelectedComm(null);
                if (onClose) onClose();
            }
        } catch (err) {
            console.error("Error during query:", err);
            setError("An error occurred while fetching results.");
        }

        setLoading(false);
    };

    return (
        <div className={styles.eastSection}>
            <h2 className={styles.title}>Add search criteria...</h2>

            {error && <div className={styles.errorMessage}>{error}</div>}

            {/* Direction selector */}
            <div className={styles.paramRow}>
                <label>Direction:</label>
                <select
                    className={styles.selectInput}
                    value={direction}
                    onChange={(e) => setDirection(e.target.value)}
                >
                    <option value="received">Received</option>
                    <option value="sent">Sent</option>
                </select>
            </div>

            {params.map((p, idx) => (
                <div key={idx} className={styles.paramRow} style={{ position: "relative" }}>
                    <select
                        className={styles.selectInput}
                        value={p.field}
                        onChange={(e) => {
                            const val = e.target.value;
                            updateParam(idx, "field", val);
                            const isSearchable = val === "subject" || val === "sender";
                            if (idx === activeParamIndex) {
                                if (!isSearchable) {
                                    setActiveParamIndex(null);
                                    setActiveParamField(null);
                                    setSearchTerm("");
                                    setSearchedResources([]);
                                } else {
                                    setActiveParamField(val);
                                    setSearchTerm(val === "subject" ? subjectDisplayNames[idx] || "" : providerDisplayNames[idx] || "");
                                    setSearchedResources([]);
                                    setError(null);
                                }
                            } else if (isSearchable) {
                                setActiveParamIndex(idx);
                                setActiveParamField(val);
                                setSearchTerm(val === "subject" ? subjectDisplayNames[idx] || "" : providerDisplayNames[idx] || "");
                                setSearchedResources([]);
                                setError(null);
                            }
                        }}
                    >
                        <option value="">Select parameter</option>
                        {COMMON_PARAMS.map((opt) => (
                            <option
                                key={opt.value}
                                value={opt.value}
                                disabled={params.some((p, i) => p.field === opt.value && i !== idx)}
                            >
                                {opt.label}
                            </option>
                        ))}
                    </select>

                    {/* Field input rendering */}
                    {p.field === "sent" ? (
                        <>
                            <input
                                type="date"
                                className={styles.dateInput}
                                placeholder="Start date"
                                value={p.startDate}
                                onChange={(e) => updateParam(idx, "startDate", e.target.value)}
                            />
                            <input
                                type="date"
                                className={styles.dateInput}
                                placeholder="End date"
                                value={p.endDate}
                                onChange={(e) => updateParam(idx, "endDate", e.target.value)}
                            />
                        </>
                    ) : p.field === "subject" || p.field === "sender" ? (
                        idx === activeParamIndex ? (
                            <>
                                <input
                                    type="text"
                                    className={styles.textInput}
                                    placeholder={`Find a ${p.field === "subject" ? "subject" : "Provider"}`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleSearchByName();
                                        }
                                    }}
                                />
                                {searchedResources.length > 0 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #ced4da",
                                            boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                                            maxHeight: "250px",
                                            overflowY: "auto",
                                            width: "calc(100% + 150px)",
                                            top: "100%",
                                            left: 0,
                                            zIndex: 1000,
                                        }}
                                    >
                                        {searchedResources.map((resource) => (
                                            <div
                                                key={resource.id}
                                                style={{ padding: "8px", cursor: "pointer" }}
                                                onClick={() => handleSelectResource(resource, idx)}
                                            >
                                                <div>
                                                    <strong>{resource.name?.[0]?.given?.join(" ")} {resource.name?.[0]?.family}</strong>
                                                </div>
                                                <div className="text-muted small">
                                                    {resource.gender ? resource.gender.charAt(0).toUpperCase() + resource.gender.slice(1) : "Unknown"}
                                                </div>
                                                <div className="text-muted small">
                                                    {resource.address?.[0]?.city || "N/A"}, {resource.address?.[0]?.state || "N/A"}
                                                </div>
                                                {p.field === "subject" && <div className="text-muted small">Birth Date: {resource.birthDate || "N/A"}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <input
                                type="text"
                                className={styles.textInput}
                                placeholder="Value"
                                value={p.field === "subject" ? subjectDisplayNames[idx] || "" : providerDisplayNames[idx] || ""}
                                readOnly
                                onFocus={() => {
                                    setActiveParamIndex(idx);
                                    setActiveParamField(p.field);
                                    setSearchTerm(p.field === "subject" ? subjectDisplayNames[idx] || "" : providerDisplayNames[idx] || "");
                                    setSearchedResources([]);
                                }}
                                style={{ cursor: "pointer", backgroundColor: "#e9ecef" }}
                            />
                        )
                    ) : p.field === "category" ? (
                        <select
                            className={styles.selectInput}
                            value={p.value}
                            onChange={(e) => updateParam(idx, "value", CATEGORY_MAP[e.target.value])}
                        >
                            <option value="">Select Category</option>
                            {Object.keys(CATEGORY_MAP).map((cat) => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className={styles.textInput}
                            placeholder="Value"
                            value={p.value}
                            onChange={(e) => updateParam(idx, "value", e.target.value)}
                        />
                    )}

                    <button className={styles.removeButton} onClick={() => removeParam(idx)}>X</button>
                </div>
            ))}

            <div className={styles.actions}>
                <button className={styles.actionButton} onClick={addParam}>Add Parameter</button>
                <button className={styles.actionButton} onClick={runQuery} disabled={loading}>
                    {loading ? "Searching..." : "Run Query"}
                </button>
            </div>
        </div>
    );
}
